import debounce from 'lodash/debounce'
import first from 'lodash/first'
import includes from 'lodash/includes'
import map from 'lodash/map'
import React, { createRef, FC, useContext, useEffect, useState } from 'react'

import { useAmplitude } from '@Analytics/hook'
import { ProductContext } from '@Components/Product/Context'
import { ImageVertical } from '@Components/Product/Slider/Components/Image/ImageVertical'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { VideoVertical } from '@Components/Product/Slider/Video'

import styles from './VerticalSlider.module.scss'

interface ArrowProps {
  direction: 'up' | 'down'
  disabled: boolean
  onClick: () => void
}

const Arrow: FC<ArrowProps> = ({
  direction,
  disabled = false,
  onClick,
}) => (
  <button
    aria-label={direction === 'up' ? 'Previous slide' : 'Next slide'}
    className={`${styles.button} ${direction === 'up' ? styles.up : styles.down}`}
    disabled={disabled}
    onClick={onClick}
    type="button"
  >
    <svg
      focusable="false"
      height="40"
      viewBox="0 0 40 40"
      width="40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M11,0L8.4,2.7L26.2,20L8.4,37.3L11,40l20.6-20L11,0z" />
    </svg>
  </button>
)

const VerticalSlider = () => {
  const {
    activeSlide,
    goToNextSlide,
    goToPreviousSlide,
    isFullscreen,
    setActiveSlide,
    slides,
  } = useContext(SliderContext)
  const product = useContext(ProductContext)
  const ampli = useAmplitude()

  const track = createRef<HTMLDivElement>()

  const [trackHasOverflow, setTrackHasOverflow] = useState(false)

  useEffect(() => {
    const recalculateVerticalTransform = (forceRecalculate = false) => {
      if (!track.current || (activeSlide.recalculate === false && !forceRecalculate)) {
        return
      }

      const clientWidth = track.current.clientWidth || 0
      const gap = 6
      const clientHeight = track.current.clientHeight || 0
      const parentHeight = track.current.parentElement?.clientHeight || 0

      if (clientHeight === parentHeight) {
        track.current.style.transform = ''
        return
      }

      const activeSlideStart = (clientWidth * activeSlide.key) + Math.max(gap * activeSlide.key, 0)
      const activeSlideCenter = activeSlideStart - (parentHeight / 2) + (clientWidth / 2)
      const minDestination = 0
      const maxDestination = clientHeight - parentHeight

      const destination = Math.min(
        Math.max(activeSlideCenter, minDestination),
        Math.max(maxDestination, minDestination),
      )

      track.current.style.transform = `translateY(-${destination}px)`

      setTrackHasOverflow(clientHeight > parentHeight)
    }

    recalculateVerticalTransform()

    const debounced = debounce(recalculateVerticalTransform)

    window.addEventListener('resize', () => debounced(true), { passive: true })

    return () => {
      window.removeEventListener('resize', () => debounced(true))
    }
  }, [activeSlide, track])

  return (
    <div className={`${styles.wrapper} ${isFullscreen ? styles.fullscreen : ''}`}>
      <Arrow
        direction="up"
        disabled={activeSlide.key === 0 || !trackHasOverflow}
        onClick={() => goToPreviousSlide(true)}
      />
      <div className={styles.track} ref={track}>
        {map(slides, (item, key) => (
          <button
            key={key}
            className={`${styles.thumbnail} ${activeSlide.key === key ? styles.active : ''}`}
            onClick={() => {
              ampli.galleryInteracted({
                asset_position: key + 1,
                asset_type: slides[key]?.tags?.includes('video') ? 'video' : 'image',
                has_look: slides?.some(slide => slide.tags?.includes('look')),
                has_video: slides?.some(slide => slide.tags?.includes('video')),
                type: 'click',
              })
              setActiveSlide({
                key,
                recalculate: true,
              })
            }}
            onFocus={() => {
              ampli.galleryInteracted({
                asset_position: key + 1,
                asset_type: slides[key]?.tags?.includes('video') ? 'video' : 'image',
                has_look: slides?.some(slide => slide.tags?.includes('look')),
                has_video: slides?.some(slide => slide.tags?.includes('video')),
                type: 'focus',
              })
              setActiveSlide({
                key,
                recalculate: true,
              })
            }}
            onMouseEnter={() => {
              ampli.galleryInteracted({
                asset_position: key + 1,
                asset_type: slides[key]?.tags?.includes('video') ? 'video' : 'image',
                has_look: slides?.some(slide => slide.tags?.includes('look')),
                has_video: slides?.some(slide => slide.tags?.includes('video')),
                type: 'hover',
              })
              setActiveSlide({
                key,
                recalculate: false,
              })
            }}
            type="button"
          >
            {
              includes(item.tags, 'video')
                ? <div className={styles.video}><VideoVertical asset={first(slides)} size={72} /></div>
                : <ImageVertical asset={item} height={72} index={key} title={product?.name || ''} width={72} />
            }
          </button>
        ))}
      </div>
      <Arrow
        direction="down"
        disabled={activeSlide.key === slides.length - 1 || !trackHasOverflow}
        onClick={() => goToNextSlide(true)}
      />
    </div>
  )
}

export default VerticalSlider
